/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo } from 'react';

/**
 * Performs lightweight client-side validation of the in-memory strategy graph.
 * Input shape:
 *  nodes: [{ id, name, params, outputs, defaultOutput }]
 *  comparisons: [{ id, lhsRef, operator, rhsKind, rhsRef, rhsValue }]
 *  sections: { KEY: { op:'AND'|'OR', children:[cmpId,...] } }
 */
export function useStrategyValidation(nodes, comparisons, sections) {
  return useMemo(() => {
    const errors = [];
    const warnings = [];

    const nodeIds = new Set(nodes.map(n => n.id));

    // 1. Node validations
    nodes.forEach(n => {
      if (!n.name) errors.push({ type:'node', id:n.id, code:'NODE_NAME_MISSING', msg:`Node ${n.id} missing name` });
      if (n.outputs && n.outputs.length && n.defaultOutput && !n.outputs.includes(n.defaultOutput)) {
        errors.push({ type:'node', id:n.id, code:'DEFAULT_OUTPUT_INVALID', msg:`Node ${n.id} defaultOutput not in outputs` });
      }
      if (n.outputs && n.outputs.length > 1 && !n.defaultOutput) {
        warnings.push({ type:'node', id:n.id, code:'DEFAULT_OUTPUT_UNSET', msg:`Node ${n.id} has multiple outputs; default not set` });
      }
      // simple empty param check
      if (n.params) {
        Object.entries(n.params).forEach(([k,v]) => {
          if (v === '' || v === null || v === undefined) {
            warnings.push({ type:'node', id:n.id, code:'PARAM_EMPTY', msg:`Node ${n.id} param '${k}' empty` });
          }
        });
      }
      // Arithmetic node validation
      if (n.type === 'arithmetic') {
        const tokens = n.tokens || [];
        if (tokens.length === 0) errors.push({ type:'node', id:n.id, code:'ARITH_EMPTY', msg:`Arithmetic ${n.id} has no tokens` });
        if (tokens[0]?.kind === 'op') errors.push({ type:'node', id:n.id, code:'ARITH_LEADING_OP', msg:`Arithmetic ${n.id} starts with operator` });
        if (tokens[tokens.length-1]?.kind === 'op') errors.push({ type:'node', id:n.id, code:'ARITH_TRAILING_OP', msg:`Arithmetic ${n.id} ends with operator` });
        for (let i=1;i<tokens.length;i++) {
          if (tokens[i].kind === 'op' && tokens[i-1].kind === 'op') {
            errors.push({ type:'node', id:n.id, code:'ARITH_DOUBLE_OP', msg:`Arithmetic ${n.id} has adjacent operators` });
          }
        }
        // Validate sources
        tokens.filter(t=>t.kind==='source').forEach(t => {
          if (typeof t.ref !== 'string' || t.ref.trim()==='') {
            errors.push({ type:'node', id:n.id, code:'ARITH_SOURCE_INVALID', msg:`Arithmetic ${n.id} has empty source ref` });
          }
        });
      }
    });

    // Helper: parse ref forms
    const resolveRef = (ref) => {
      if (!ref) return null;
      if (ref.includes(':')) {
        const [nid, out] = ref.split(':');
        return { nodeId: nid, output: out || null };
      }
      return { nodeId: ref, output: null };
    };

    const nodeLookup = Object.fromEntries(nodes.map(n => [n.id, n]));

    // 2. Comparison validations
    comparisons.forEach(c => {
      // New shape: {id,type:'comparison', lhs, operator, rhs}
      const lhs = c.lhs;
      const rhs = c.rhs;
      if (!lhs && lhs !== 0) errors.push({ type:'comparison', id:c.id, code:'LHS_MISSING', msg:`Comparison ${c.id} missing LHS` });
      if (!c.operator) errors.push({ type:'comparison', id:c.id, code:'OP_MISSING', msg:`Comparison ${c.id} missing operator` });

      // Validate RHS presence except for operators that might allow unary (not currently supported)
      if (rhs === undefined) errors.push({ type:'comparison', id:c.id, code:'RHS_MISSING', msg:`Comparison ${c.id} missing RHS` });

      const refStrings = [];
      if (typeof lhs === 'string') refStrings.push(lhs);
      if (typeof rhs === 'string') refStrings.push(rhs);

      refStrings.forEach(ref => {
        const parsed = resolveRef(ref);
        if (!parsed) return;
        if (!nodeIds.has(parsed.nodeId)) {
          errors.push({ type:'comparison', id:c.id, code:'REF_NODE_UNKNOWN', msg:`Comparison ${c.id} references missing node ${parsed.nodeId}` });
        } else if (parsed.output) {
          const n = nodeLookup[parsed.nodeId];
          if (!(n.outputs||[]).includes(parsed.output)) {
            errors.push({ type:'comparison', id:c.id, code:'REF_OUTPUT_UNKNOWN', msg:`Comparison ${c.id} references missing output ${parsed.output} on node ${parsed.nodeId}` });
          }
        }
      });

      // Inline raw operand validation
      [lhs, rhs].filter(v => typeof v === 'object' && v && v.type === 'price').forEach(op => {
        if (!op.field && !op.source) {
          errors.push({ type:'comparison', id:c.id, code:'RAW_FIELD_MISSING', msg:`Comparison ${c.id} raw operand missing field` });
        }
        const fld = op.field || op.source;
        if (!['open','high','low','close','volume'].includes(fld)) {
          errors.push({ type:'comparison', id:c.id, code:'RAW_FIELD_INVALID', msg:`Comparison ${c.id} raw field '${fld}' invalid` });
        }
      });
    });

  const comparisonIds = new Set(comparisons.map(c => c.id));

    // 3a. Expression node formula validation
    const formulaRefRegex = /\b([a-zA-Z]\w*)(?:\.(\w+))?\b/g; // matches nodeId or nodeId.output
    nodes.filter(n => n.kind === 'expression').forEach(n => {
      const formula = n.params?.formula || '';
      if (!formula.trim()) {
        errors.push({ type:'node', id:n.id, code:'FORMULA_EMPTY', msg:`Expression ${n.id} formula empty` });
        return;
      }
      const seen = new Set();
      let match;
      while ((match = formulaRefRegex.exec(formula))){
        const refNodeId = match[1];
        const refOutput = match[2] || null;
        const key = refNodeId + ':' + (refOutput||'');
        if (seen.has(key)) continue; // avoid dup messages
        seen.add(key);
        if (!nodeIds.has(refNodeId)) {
          errors.push({ type:'node', id:n.id, code:'FORMULA_NODE_UNKNOWN', msg:`Expression ${n.id} references missing node ${refNodeId}` });
        } else if (refOutput) {
          const targetNode = nodeLookup[refNodeId];
          if ((targetNode.outputs||[]).length > 0 && !(targetNode.outputs||[]).includes(refOutput)) {
            errors.push({ type:'node', id:n.id, code:'FORMULA_OUTPUT_UNKNOWN', msg:`Expression ${n.id} references missing output ${refOutput} on ${refNodeId}` });
          }
        }
      }
    });

    // 3b. Section validations
    Object.entries(sections).forEach(([key, sec]) => {
      if (!sec || !Array.isArray(sec.children)) return;
      sec.children.forEach(cid => {
        if (!comparisonIds.has(cid)) {
          errors.push({ type:'section', id:key, code:'SECTION_COMPARISON_MISSING', msg:`Section ${key} references missing comparison ${cid}` });
        }
      });
      if (sec.children.length === 0) {
        warnings.push({ type:'section', id:key, code:'SECTION_EMPTY', msg:`Section ${key} has no comparisons` });
      }
    });

    // 4. Derived summary
    const summary = {
      errorCount: errors.length,
      warningCount: warnings.length,
      isValid: errors.length === 0,
    };

    return { errors, warnings, summary };
  }, [nodes, comparisons, sections]);
}
